import React, { Suspense, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
	BrowserRouter,
	Redirect,
	Route,
	Switch,
	useLocation,
} from "react-router-dom";
import TopLoadingBar from "react-top-loading-bar";
import PrivateRoute from "./PrivateRoute";
import CustomErrorPage from "./components/CustomErrorPage";
import SignIn from "./containers/SignIn";
import Confirmation from "./containers/common/Confirmation";
import Layout from "./containers/layout";
import useMatomoPageViewTracker from "./hooks/useMatomoTracking";
import lazy from "./utils/lazy-with-reload";

const LinkedInPopUp = lazy(() =>
	import("react-linkedin-login-oauth2").then((module) => ({
		default: module.LinkedInPopUp,
	})),
);
const SmsStart = lazy(() => import("./components/newUI/SmsCampaign/SmsStart"));
const PreviewIframe = lazy(() => import("./components/newUI/Templates/PreviewIframe"));
const CustomColumns = lazy(() => import("./containers/CustomColumns"));
const TextEditor = lazy(() => import("./containers/campaign/TextEditor"));
const Sms = lazy(
	() => import("./containers/smsconfiguration/SmsConfigurationDashboard"),
);
const Conversations = lazy(() => import("./components/Conversations"));
const EmailAlreadyVerified = lazy(
	() => import("./components/EmailAlreadyVerified"),
);
const EmailTemplateApproval = lazy(
	() => import("./components/EmailTemplateApproval"),
);
const HomeDashboardComponent = lazy(() => import("./components/HomeDashboard"));
const IndividualLandingPageReport = lazy(
	() => import("./components/IndividualLandingPageReport"),
);
const PublishPostModal = lazy(() => import("./components/PublishPostModal"));
const TemplateModerationStatus = lazy(
	() => import("./components/TemplateModerationStatus"),
);
const CampaignReport = lazy(
	() => import("./components/camagin/Campaignreport"),
);
const FactCubes = lazy(() => import("./components/factcubes/factcubes"));

const Account = lazy(() => import("./components/newUI/AccountBilling/Account"));
const Billing = lazy(() => import("./components/newUI/AccountBilling/Billing"));
const Invoices = lazy(
	() => import("./components/newUI/AccountBilling/Invoices"),
);
const AiAgentMessagingTab = lazy(
	() => import("./components/newUI/BusinessMessaging/AiAgentMessagingTab"),
);
const BusinessMessagingTab = lazy(
	() => import("./components/newUI/BusinessMessaging/BusinessMessagingTab"),
);
const ConversationMessaging = lazy(
	() => import("./components/newUI/BusinessMessaging/ConversationMessaging"),
);
const ConversationRules = lazy(
	() => import("./components/newUI/BusinessMessaging/ConversationRules"),
);
const AdCampaign = lazy(
	() => import("./components/newUI/Campaigns/AdCampaign"),
);
const EmailCampaign = lazy(
	() => import("./components/newUI/Campaigns/EmailCampaign"),
);
const SMSCampaign = lazy(
	() => import("./components/newUI/Campaigns/SMSCampaign"),
);
const SocialCampaign = lazy(
	() => import("./components/newUI/Campaigns/SocialCampaign"),
);
const NewEmailCampaign = lazy(() => import("./components/newUI/EmailCampaign"));
const FanMaturityModel = lazy(
	() => import("./components/newUI/FanMaturityModel"),
);
const HomeDashboard = lazy(
	() => import("./components/newUI/HomeScreen/HomeDashboard"),
);
const LandingPage = lazy(() => import("./components/newUI/LandingPages"));
const PlanDetails = lazy(() => import("./components/newUI/PlanDetails/index"));
const ChooseAddons = lazy(
	() => import("./components/newUI/PricingDetails/ChooseAddons"),
);
const PaymentStatus = lazy(
	() => import("./components/newUI/PricingDetails/PaymentStatus"),
);
const Review = lazy(() => import("./components/newUI/PricingDetails/Review"));
const SelectCapacity = lazy(
	() => import("./components/newUI/PricingDetails/SelectCapacity"),
);
const AllContactLists = lazy(
	() => import("./components/newUI/Segments/AllContactLists"),
);
const Contacts = lazy(() => import("./components/newUI/Segments/Contacts"));
const SegmentsDashboard = lazy(
	() => import("./components/newUI/Segments/SegmentsDashboard"),
);
const UploadHistory = lazy(
	() => import("./components/newUI/Segments/UploadHistory"),
);
const AccountsAndBilling = lazy(
	() => import("./components/newUI/Settings/AccountsAndUsers"),
);
const APIIntegrationsReport = lazy(
	() => import("./components/newUI/Settings/ApiIntegrationReport"),
);
const APIIntegrations = lazy(
	() => import("./components/newUI/Settings/ApiIntegrations"),
);
const Channels = lazy(() => import("./components/newUI/Settings/Channels"));
const FactCubesSettings = lazy(
	() => import("./components/newUI/Settings/FactcubesSettings"),
);
const SettingsIntegrations = lazy(
	() => import("./components/newUI/Settings/Integrations"),
);
const Rules = lazy(() => import("./components/newUI/Settings/Rules"));
const SocialMedia = lazy(
	() => import("./components/newUI/Settings/SocialMedia"),
);
const WhatsappConfig = lazy(
	() => import("./components/newUI/Settings/WhatsappConfig"),
);
const SmsCompose = lazy(
	() => import("./components/newUI/SmsCampaign/SmsCompose"),
);
const SmsReviewAndLaunch = lazy(
	() => import("./components/newUI/SmsCampaign/SmsReviewAndLaunch"),
);
const EmailTemplates = lazy(
	() => import("./components/newUI/Templates/EmailTemplates"),
);
const InstagramTemplates = lazy(
	() => import("./components/newUI/Templates/InstagramTemplates"),
);
const SMSTemplates = lazy(
	() => import("./components/newUI/Templates/SMSTemplates"),
);
const WebPushTemplates = lazy(
	() => import("./components/newUI/Templates/WebPushTemplates"),
);
const WhatsAppTemplates = lazy(
	() => import("./components/newUI/Templates/WhatsAppTemplates"),
);
const UpgradeContacts = lazy(
	() => import("./components/newUI/UpgradeContacts"),
);
const AccountCreated = lazy(
	() => import("./components/newUI/authflow/AccountCreated"),
);
const NewCreatePassword = lazy(
	() => import("./components/newUI/authflow/CreatePassword"),
);
const NewSignUp = lazy(() => import("./components/newUI/authflow/Signup"));
const BotTriggers = lazy(
	() => import("./components/newUI/conversations/BotTriggers"),
);
const ConversationIGMessaging = lazy(
	() => import("./components/newUI/conversations/ConversationIGMessaging"),
);
const Icebreakers = lazy(
	() => import("./components/newUI/conversations/Icebreakers"),
);
const PersistentMenu = lazy(
	() => import("./components/newUI/conversations/PersistentMenu"),
);
const AccessLandingPage = lazy(() => import("./containers/AccessLandingPage"));
const AddNewUser = lazy(() => import("./containers/Addnewuser"));
const AddNewUserPassword = lazy(
	() => import("./containers/Addnewuserpassword"),
);
const AudienceDashboard = lazy(() => import("./containers/AudienceDashboard"));
const Campaign = lazy(() => import("./containers/Campaign_n"));
const CartAbandonSetting = lazy(
	() => import("./containers/CartAbandonSetting"),
);
const Contact = lazy(() => import("./containers/ContactReport"));
const AddContacts = lazy(() => import("./containers/Contacts"));
const ConversationalBot = lazy(() => import("./containers/ConversationalBot"));
const CustomFieldsCreation = lazy(
	() => import("./containers/Customfieldcreation"),
);
const CustomFields = lazy(() => import("./containers/Customfields"));
const DeleteAccount = lazy(() => import("./containers/DeleteAccount"));
const EmailConfirmed = lazy(() => import("./containers/EmailConfirmed"));
const EmailUnlayerEditor = lazy(
	() => import("./containers/EmailUnlayerEditor"),
);
const EngagementRules = lazy(() => import("./containers/EngagementRules"));
const FB = lazy(() => import("./containers/FB"));
const ForgotPassword = lazy(() => import("./containers/ForgotPassword"));
const FormCreator = lazy(() => import("./containers/FormCreator"));
const Integrations = lazy(() => import("./containers/Integrations"));
const LandingPageList = lazy(() => import("./containers/LandingPageList"));
const LandingPagePublish = lazy(
	() => import("./containers/LandingPagePublish"),
);
const LandingPageSettings = lazy(
	() => import("./containers/LandingPageSettings"),
);
const LandingPageUtility = lazy(
	() => import("./containers/LandingPageUtility"),
);
const MarketingCalendar = lazy(
	() => import("./containers/MarketingCalendar/MarketingCalendar"),
);
const OnboardEnterprise = lazy(() => import("./containers/OnboardEnterprise"));
const OnboardGoal = lazy(() => import("./containers/OnboardGoal"));
const RenderForm = lazy(() => import("./containers/RenderForm"));
const ResetPassword = lazy(() => import("./containers/ResetPassword"));
const SetPassword = lazy(() => import("./containers/SetPassword"));
const Shopify = lazy(() => import("./containers/Shopify"));
const SmsSenderSetting = lazy(() => import("./containers/Smssenderdetails"));
const SocialLoginCallback = lazy(
	() => import("./containers/SocialLoginCallback"),
);
const UnlayerEditor = lazy(() => import("./containers/UnlayerEditor"));
const Upload = lazy(() => import("./containers/Upload"));
const AdsDashboards = lazy(() => import("./containers/ads/AdsDashboards"));
const Audience = lazy(() => import("./containers/audience/Audience"));
const AutosegmentConfig = lazy(
	() => import("./containers/autosegment/AutosegmentConfig"),
);
const CarrierFiltration = lazy(
	() => import("./containers/campaign/CarreirFilteration"),
);
const SkipReports = lazy(() => import("./containers/campaign/SkipReports"));
const ViewEmailSummary = lazy(
	() => import("./containers/campaign/ViewEmailSummary"),
);
const EmailClickData = lazy(
	() => import("./containers/campaign/clickDataCampaign"),
);
const EmailCampaignDepartmentStore = lazy(
	() => import("./containers/campaign/individualCampaign"),
);
const EmailJourneyCampaignDepartmentStore = lazy(
	() => import("./containers/campaign/individualJourneyCampaign"),
);
const SmsJourneyCampaignDepartmentStore = lazy(
	() => import("./containers/campaign/individualJourneySmsCampaign"),
);
const IndividualSmsCampaign = lazy(
	() => import("./containers/campaign/individualSmsCampaign"),
);
const IndividualWhatsAppCampaign = lazy(
	() => import("./containers/campaign/individualWhatsAppCampaign"),
);
const SocialAdCampaignViewDetails = lazy(
	() => import("./containers/campaign/viewDetails"),
);
const DndSettings = lazy(
	() => import("./containers/configuration/DndSettings"),
);
const EmailConfiguration = lazy(
	() => import("./containers/configuration/EmailConfiguration"),
);
const EmailCampaignViewDesign = lazy(
	() => import("./containers/campaign/viewDesign"),
);
const WebsiteConfiguration = lazy(
	() => import("./containers/configuration/WebsiteConfiguration"),
);

const ContactView = lazy(() => import("./containers/contact/ContactView"));
const ListImportHistory = lazy(
	() => import("./containers/contact/ListImportHistory"),
);
const SingleContact = lazy(() => import("./containers/contact/SingleContact"));
const WhatsappTemplateStatus = lazy(
	() => import("./containers/whatsapp_common/WhatsappTemplateStatus"),
);
const AddManually = lazy(
	() => import("./containers/customobjects/Addmanually"),
);
const CreateCustomObjects = lazy(
	() => import("./containers/customobjects/Createcustomobject"),
);
const CustomObjects = lazy(
	() => import("./containers/customobjects/Customobject"),
);
const CustomObjectUpload = lazy(
	() => import("./containers/customobjects/Customupload"),
);
const FormRender = lazy(() => import("./containers/formrender/FormRender"));
const IndividualExternalAppPushReport = lazy(
	() => import("./containers/journey_builder/IndividualExternalAppPushReport"),
);
const IndividualMobilepushReport = lazy(
	() => import("./containers/journey_builder/IndividualMobilepushReport"),
);
const IndividualWebpushReport = lazy(
	() => import("./containers/journey_builder/IndividualWebpushReport"),
);
const JourneyBuilder = lazy(
	() => import("./containers/journey_builder/JourneyBuilder"),
);
const JourneyDashboard = lazy(
	() => import("./containers/journey_builder/JourneyDashboard"),
);
const JourneyOverview = lazy(
	() => import("./containers/journey_builder/JourneyOverview"),
);
const JourneyReview = lazy(
	() => import("./containers/journey_builder/JourneyReview"),
);
const JourneySummary = lazy(
	() => import("./containers/journey_builder/JourneySummary"),
);
const JourneyTemplatesDashboard = lazy(
	() =>
		import(
			"./containers/journey_builder/journeyTemplates/JourneyTemplatesDashboard"
		),
);
const JourneyTemplateInfoDashboard = lazy(
	() =>
		import(
			"./containers/journey_builder/journeyTemplatesInfo/JourneyTemplateInfoDashboard"
		),
);
const AllNotifications = lazy(() =>
	import("./containers/notifications/index").then((module) => ({
		default: module.AllNotifications,
	})),
);
const ChangePassword = lazy(() =>
	import("./containers/notifications/index").then((module) => ({
		default: module.ChangePassword,
	})),
);
const PaymentCancel = lazy(() =>
	import("./containers/notifications/index").then((module) => ({
		default: module.PaymentCancel,
	})),
);
const PaymentSuccess = lazy(() =>
	import("./containers/notifications/index").then((module) => ({
		default: module.PaymentSuccess,
	})),
);
const UpgradeDashboard = lazy(() =>
	import("./containers/notifications/index").then((module) => ({
		default: module.UpgradeDashboard,
	})),
);
const Wallet = lazy(() =>
	import("./containers/notifications/index").then((module) => ({
		default: module.Wallet,
	})),
);
const Selection = lazy(() => import("./containers/selection"));
const SmsSettings = lazy(() => import("./containers/sms/SmsSettings"));
const SmsTemplateStatus = lazy(
	() => import("./containers/sms_common/SmsTemplateStatus"),
);
const ComplianceReport = lazy(
	() => import("./containers/sms_reports/ComplianceReport"),
);
const SMSCampaignViewDesign = lazy(
	() => import("./containers/smsconfiguration/ViewDesign"),
);

function AppWithLoader() {
	useMatomoPageViewTracker()
	/** @type {React.LegacyRef<import("react-top-loading-bar").LoadingBarRef> } */
	const loadingBarRef = useRef(null);
	const location = useLocation();
	const [prevLocation, setPrevLocation] = useState(null);

	if (location.pathname !== prevLocation?.pathname) {
		loadingBarRef.current?.continuousStart();
		setPrevLocation(location);
		setTimeout(() => loadingBarRef.current?.complete(), 1000);
	}

	return (
		<Layout>
			<TopLoadingBar
				color="#646BDB"
				ref={loadingBarRef}
				shadow={true}
				shadowStyle={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)" }}
				height={2}
			/>
			<Suspense
				fallback={
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100vh",
							width: "100vw",
							background: "#f0f0f0",
							color: "#000",
							fontSize: "20px",
						}}
					/>
				}
			>
				<Switch>
					<PrivateRoute exact path="/" component={HomeDashboardComponent} />
					<PrivateRoute exact path="/audience" component={Audience} />
					<PrivateRoute exact path="/audience/:id" component={Audience} />
					<PrivateRoute exact path="/onboard1" component={OnboardEnterprise} />
					<PrivateRoute exact path="/onboard2" component={OnboardGoal} />
					<Route exact path="/customcolumns" component={CustomColumns} />
					<Route exact path="/form" component={FormCreator} />
					<Route exact path="/form/render" component={RenderForm} />
					<Route exact path="/linkedin" component={LinkedInPopUp} />
					<PrivateRoute
						exact
						path="/settings/social-config"
						component={SocialMedia}
					/>
					<PrivateRoute
						exact
						path="/settings/autosegment"
						component={AutosegmentConfig}
					/>
					<PrivateRoute exact path="/newemailc" component={NewEmailCampaign} />
					<PrivateRoute
						exact
						path="/newemailc/:id"
						component={NewEmailCampaign}
					/>
					<PrivateRoute
						exact
						path="/newemailc/:id/:stage/"
						component={NewEmailCampaign}
					/>
					<PrivateRoute
						exact
						path="/newemailc/:id/:stage/:stageID"
						component={NewEmailCampaign}
					/>
					<PrivateRoute
						exact
						path="/newemailc/:id/reviewsubmit/:data_id"
						component={NewEmailCampaign}
					/>

					<PrivateRoute
						exact
						path="/settings/website-config"
						component={WebsiteConfiguration}
					/>
					<PrivateRoute
						exact
						path="/settings/email-config"
						component={EmailConfiguration}
					/>
					<PrivateRoute
						exact
						path="/settings/api-integrations"
						component={APIIntegrations}
					/>
					<PrivateRoute
						exact
						path="/settings/api-integrations/report"
						component={APIIntegrationsReport}
					/>
					<PrivateRoute
						exact
						path="/settings/whatsapp-config"
						component={WhatsappConfig}
					/>
					<Route exact path="/socialmedia/facebook" component={FB} />
					<Route exact path="/socialmedia/facebook/:id" component={FB} />
					<Route exact path="/contact-list/upload" component={Upload} />
					<Route exact path="/contact-list" component={Contact} />
					<Route exact path="/signin" component={SignIn} />
					<Route exact path="/signin/:id" component={SignIn} />
					<Route exact path="/signin/:tab" component={SignIn} />
					<Route exact path="/email-confirmed" component={EmailConfirmed} />
					<Route
						exact
						path="/emailverified"
						render={(props) => <EmailAlreadyVerified {...props} />}
					/>
					<PrivateRoute exact path="/settings" component={Channels} />
					<PrivateRoute
						exact
						path="/settings/account-and-billing"
						component={Account}
					/>
					<PrivateRoute exact path="/settings/account" component={Account} />
					<PrivateRoute exact path="/settings/billing" component={Billing} />
					<PrivateRoute
						exact
						path="/settings/account-and-billing/invoices"
						component={Invoices}
					/>
					<PrivateRoute
						exact
						path="/settings/plan-details"
						component={PlanDetails}
					/>
					<PrivateRoute
						exact
						path="/settings/pricing-details/selectcapacity"
						component={SelectCapacity}
					/>
					<PrivateRoute
						exact
						path="/settings/pricing-details/chooseaddons"
						component={ChooseAddons}
					/>
					<PrivateRoute
						exact
						path="/settings/pricing-details/review"
						component={Review}
					/>
					<PrivateRoute
						exact
						path="/settings/pricing-details/payment-info"
						component={PaymentStatus}
					/>
					<PrivateRoute exact path="/landing_pages" component={LandingPage} />
					<PrivateRoute
						exact
						path="/settings/upgrade-contacts/1"
						component={UpgradeContacts}
					/>
					<PrivateRoute
						exact
						path="/settings/app-integrations"
						component={Integrations}
					/>
					<PrivateRoute
						exact
						path="/settings/configure-engagement"
						component={EngagementRules}
					/>
					<PrivateRoute
						exact
						path="/settings/cart-abandon"
						component={CartAbandonSetting}
					/>
					<PrivateRoute
						exact
						path="/settings/app-integrations/shopify"
						component={Shopify}
					/>
					<Route exact path="/forgot-password" component={ForgotPassword} />
					<Route exact path="/preview" component={PreviewIframe} />
					<Route exact path="/resetpassword" component={ResetPassword} />
					<Route
						exact
						path="/contact-list/add-contact"
						component={AddContacts}
					/>
					<PrivateRoute exact path="/campaign" component={EmailCampaign} />
					<PrivateRoute
						exact
						path="/campaign/socialpost"
						component={SocialCampaign}
					/>
					<PrivateRoute exact path="/campaign/sms" component={SMSCampaign} />
					<PrivateRoute
						exact
						path="/campaign/socialad"
						component={AdCampaign}
					/>
					<Route
						exact
						path="/carrier-filteration"
						component={CarrierFiltration}
					/>
					<Route exact path="/selection" component={Selection} />
					<Route exact path="/set-password" component={SetPassword} />
					<Route exact path="/publishpostmodal" component={PublishPostModal} />
					<PrivateRoute exact path="/journey" component={JourneyDashboard} />
					<PrivateRoute component={HomeDashboard} path="/" exact />
					<PrivateRoute
						exact
						path="/journey-overview/:id"
						component={JourneyOverview}
					/>
					<PrivateRoute
						exact
						path="/journey-overview/review/:id"
						component={JourneyReview}
					/>
					<Route exact path="/journey-builder/:id" component={JourneyBuilder} />
					<Route
						exact
						path="/journey-buildera/:id/:id"
						component={JourneyBuilder}
					/>
					<PrivateRoute exact path="/onboard2/:id" component={OnboardGoal} />
					<PrivateRoute exact path="/campaign/start" component={Campaign} />
					<PrivateRoute exact path="/smscampaign/start" component={SmsStart} />
					<PrivateRoute
						exact
						path="/smscampaign/start/:id"
						component={SmsStart}
					/>
					<PrivateRoute
						exact
						path="/smscampaign/start/:id/:stage/"
						component={SmsStart}
					/>
					<PrivateRoute
						exact
						path="/smscampaign/start/:id/reviewsubmit/:data_id"
						component={SmsStart}
					/>
					<PrivateRoute
						exact
						path="/smscampaign/compose"
						component={SmsCompose}
					/>
					<PrivateRoute
						exact
						path="/smscampaign/review-and-launch"
						component={SmsReviewAndLaunch}
					/>
					<PrivateRoute exact path="/campaign/:id/:id/" component={Campaign} />
					<Route exact path="/smscampaign/:id/:id/" component={Sms} />
					<PrivateRoute
						exact
						path="/campaign/:id/:id/:id"
						component={Campaign}
					/>
					<Route exact path="/smscampaign/:id/:id/:id" component={Sms} />
					<Route exact path="/campaign-ads/:id" component={AdsDashboards} />
					<Route exact path="/campaign-ads/:id/:id" component={AdsDashboards} />
					<Route exact path="/app_forms" component={FormRender} />
					<PrivateRoute
						exact
						path="/contact-list/single-contact"
						component={SingleContact}
					/>
					<PrivateRoute
						exact
						path="/contact-list/single-contact/:id"
						component={SingleContact}
					/>
					<PrivateRoute
						exact
						path="/settings/customfields"
						component={CustomFields}
					/>
					<PrivateRoute
						exact
						path="/settings/sms-settings"
						component={SmsSettings}
					/>
					<PrivateRoute
						exact
						path="/settings/dnd-settings"
						component={DndSettings}
					/>
					<PrivateRoute
						exact
						path="/settings/customfieldscreation"
						component={CustomFieldsCreation}
					/>
					<PrivateRoute
						exact
						path="/settings/customfieldscreation/:id"
						component={CustomFieldsCreation}
					/>
					<PrivateRoute
						exact
						path="/campaign/:id/start/drag-and-drop/:id"
						component={UnlayerEditor}
					/>
					<PrivateRoute
						exact
						path="/campaign/:id/compose/:id/template-editor/:id"
						component={UnlayerEditor}
					/>
					<PrivateRoute
						exact
						path="/create-template/template/:id"
						component={UnlayerEditor}
					/>
					<Route
						exact
						path="/callback/:provider"
						component={SocialLoginCallback}
					/>
					<Route
						exact
						path="/journey-builder/:id/:id/edit-template/:id"
						component={EmailUnlayerEditor}
					/>
					<Route
						exact
						path="/journey-builder/:id/:id/start-from-scratch"
						component={EmailUnlayerEditor}
					/>
					<PrivateRoute
						exact
						path="/journey-builder/:id/summary"
						component={JourneySummary}
					/>
					<PrivateRoute exact path="/settings/channels" component={Channels} />
					<PrivateRoute
						exact
						path="/settings/integrations"
						component={SettingsIntegrations}
					/>
					<PrivateRoute exact path="/settings/rules" component={Rules} />
					<PrivateRoute
						exact
						path="/settings/account-and-users"
						component={AccountsAndBilling}
					/>
					<PrivateRoute
						exact
						path="/settings/conversation_rules"
						component={ConversationRules}
					/>
					<PrivateRoute
						exact
						path="/settings/conversation_rules/conversation_messaging"
						component={ConversationMessaging}
					/>
					<PrivateRoute
						exact
						path="/settings/conversation_rules/business_messaging"
						component={BusinessMessagingTab}
					/>
					<PrivateRoute
						exact
						path="/settings/conversation_rules/ai_agents"
						component={AiAgentMessagingTab}
					/>
					<Route
						exact
						path="/individualCampaign/email/:id"
						component={EmailCampaignDepartmentStore}
					/>
					<Route
						exact
						path="/individualCampaign/sms/:id"
						component={IndividualSmsCampaign}
					/>
					<Route
						exact
						path="/MarketingCalendar"
						component={MarketingCalendar}
					/>
					<Route
						exact
						path="/landingPages/:id"
						component={IndividualLandingPageReport}
					/>
					<Route
						exact
						path="/individualCampaign/component/:id/sms/:reportType/:journeyId"
						component={IndividualSmsCampaign}
					/>
					,
					<Route
						exact
						path="/individualCampaign/whatsapp/:id"
						component={IndividualWhatsAppCampaign}
					/>
					<Route
						exact
						path="/individualCampaign/component/:id/whatsapp/:reportType/:journeyId"
						component={IndividualWhatsAppCampaign}
					/>
					<Route
						exact
						path="/individualCampaign/journey-sms/:id"
						component={SmsJourneyCampaignDepartmentStore}
					/>
					<Route
						exact
						path="/viewDesign/sms/:id"
						component={SMSCampaignViewDesign}
					/>
					<PrivateRoute
						exact
						path="/viewDesign/email/:id"
						component={ViewEmailSummary}
					/>
					<Route
						exact
						path="/viewDesign/template/:id/journey/:journeyId"
						component={EmailCampaignViewDesign}
					/>
					<Route
						exact
						path="/viewDetails/campaign-ads/:id"
						component={SocialAdCampaignViewDetails}
					/>
					<Route exact path="/clickData/email/:id" component={EmailClickData} />
					<PrivateRoute
						exact
						path="/skip_report/:asset_type/:id"
						component={SkipReports}
					/>
					<Route exact path="/texteditor" component={TextEditor} />
					<Route exact path="/campaignreport" component={CampaignReport} />
					<PrivateRoute exact path="/addnewuser" component={AddNewUser} />
					<Route
						exact
						path="/addnewuserpassword"
						component={AddNewUserPassword}
					/>
					<Route exact path="/texteditor/:id" component={TextEditor} />
					<Route
						exact
						path="/individualCampaign/social-post/:id"
						component={EmailCampaignDepartmentStore}
					/>
					<Route
						exact
						path="/individualCampaign/social-ads/:id"
						component={EmailCampaignDepartmentStore}
					/>
					<PrivateRoute exact path="/segments" component={SegmentsDashboard} />
					<PrivateRoute exact path="/segments/contacts" component={Contacts} />
					<PrivateRoute
						exact
						path="/segments/lists"
						component={AllContactLists}
					/>
					<PrivateRoute
						exact
						path="/segments/audience"
						component={AudienceDashboard}
					/>
					<PrivateRoute
						exact
						path="/segments/upload-history"
						component={UploadHistory}
					/>
					<PrivateRoute exact path="/segments/:id" component={ContactView} />
					<PrivateRoute
						exact
						path="/contact-list/history/:id"
						component={ListImportHistory}
					/>
					<Route
						exact
						path="/journey-templates/:id"
						component={JourneyTemplatesDashboard}
					/>
					<Route
						exact
						path="/journey-templates/:id/:id"
						component={JourneyTemplatesDashboard}
					/>
					<PrivateRoute
						exact
						path="/accountsettings"
						component={UpgradeDashboard}
					/>
					<Route exact path="/notifications" component={AllNotifications} />
					<PrivateRoute exact path="/wallet" component={Wallet} />
					<Route
						exact
						path="/fetch-template"
						component={JourneyTemplateInfoDashboard}
					/>
					<PrivateRoute
						exact
						path="/paymentsuccess/:id"
						component={PaymentSuccess}
					/>
					<PrivateRoute exact path="/paymenterror" component={PaymentCancel} />
					<PrivateRoute
						exact
						path="/changepassword"
						component={ChangePassword}
					/>
					<PrivateRoute exact path="/deleteaccount" component={DeleteAccount} />
					<PrivateRoute exact path="/templates" component={EmailTemplates} />
					<PrivateRoute exact path="/templates/sms" component={SMSTemplates} />
					<PrivateRoute
						exact
						path="/templates/whatsapp"
						component={WhatsAppTemplates}
					/>
					<PrivateRoute
						exact
						path="/templates/social-conversation"
						component={InstagramTemplates}
					/>
					<PrivateRoute
						exact
						path="/templates/webpush"
						component={WebPushTemplates}
					/>
					<Route
						exact
						path="/edit-template/:id"
						component={EmailUnlayerEditor}
					/>
					<Route
						exact
						path="/edit-factoreal-template/:id"
						component={EmailUnlayerEditor}
					/>
					<Route
						exact
						path="/create-templates"
						component={EmailUnlayerEditor}
					/>
					<Route
						exact
						path="/individualCampaign/component/:id/email/:reportType/:journeyId"
						component={EmailJourneyCampaignDepartmentStore}
					/>
					<Route
						exact
						path="/individualCampaign/social-post/:id/:reportType/:journeyId"
						component={EmailCampaignDepartmentStore}
					/>
					<Route
						exact
						path="/individualCampaign/social-ads/:id/:reportType/:journeyId"
						component={EmailCampaignDepartmentStore}
					/>
					<Route
						exact
						path="/template-status/:id"
						component={TemplateModerationStatus}
					/>
					<Route
						exact
						path="/template-approval-status/:id"
						component={EmailTemplateApproval}
					/>
					<Route
						exact
						path="/whatsapptemplate-status/:id"
						component={WhatsappTemplateStatus}
					/>
					<Route
						exact
						path="/clickData/journey/email/url/:journeyId"
						component={EmailClickData}
					/>
					<Route exact path="/factcube" component={FactCubes} />

					{/* Removing metabase dashboard */}
					<Route exact path="/sms-campaign/:id" component={Sms} />
					<Route exact path="/sms-campaign/:id/:id" component={Sms} />
					<Route
						exact
						path="/messaging-template-status/:id"
						component={SmsTemplateStatus}
					/>
					<Route
						exact
						path="/settings/smssetting"
						component={SmsSenderSetting}
					/>
					<Route
						exact
						path="/edit-landing-page/:id"
						component={LandingPageUtility}
					/>
					<Route
						exact
						path="/landing-page-editor"
						component={LandingPageUtility}
					/>
					<Route
						exact
						path="/landing-page-publish/:id"
						component={LandingPagePublish}
					/>
					<Route exact path="/landing-page" component={AccessLandingPage} />
					<Route
						exact
						path="/landing-page-list/:id"
						component={LandingPageList}
					/>
					<Route
						exact
						path="/landing-page-settings/:id"
						component={LandingPageSettings}
					/>
					<Route exact path="/customobjects" component={CustomObjects} />
					<Route
						exact
						path="/customobjectsupload/upload"
						component={CustomObjectUpload}
					/>
					<Route
						exact
						path="/customobjectsupload/addmanually"
						component={AddManually}
					/>
					<Route
						exact
						path="/customobjectsupload/addmanually/:id"
						component={AddManually}
					/>
					<Route
						exact
						path="/createcustomobject"
						component={CreateCustomObjects}
					/>
					<Route
						exact
						path="/editcustomobject/:id"
						component={CreateCustomObjects}
					/>
					<Route
						exact
						path="/report/compliance-report"
						component={ComplianceReport}
					/>
					<Route
						exact
						path="/individual/:reportType/webpush/:journeyId/:componentId"
						component={IndividualWebpushReport}
					/>
					<Route
						exact
						path="/individual/:reportType/mobilepush/:journeyId/:componentId"
						component={IndividualMobilepushReport}
					/>
					<Route
						exact
						path="/individual/:reportType/externalpush/:journeyId/:componentId"
						component={IndividualExternalAppPushReport}
					/>
					<Route
						exact
						path="/account-created"
						render={(props) => <AccountCreated {...props} />}
					/>
					<Route
						exact
						path="/signup"
						render={(props) => <NewSignUp {...props} />}
					/>
					<Route
						exact
						path="/create-password"
						render={(props) => <NewCreatePassword {...props} />}
					/>
					<PrivateRoute exact path="/conversations" component={Conversations} />
					<PrivateRoute
						exact
						path="/conversations/social-messaging"
						component={ConversationIGMessaging}
					/>
					<PrivateRoute
						exact
						path="/conversations/social-messaging/icebreakers"
						component={Icebreakers}
					/>
					<PrivateRoute
						exact
						path="/conversations/social-messaging/persistent-menu"
						component={PersistentMenu}
					/>
					<PrivateRoute
						exact
						path="/conversations/social-messaging/bot-triggers"
						component={BotTriggers}
					/>
					<PrivateRoute
						exact
						path="/conversational-bot"
						component={ConversationalBot}
					/>
					<PrivateRoute
						exact
						path="/settings/fmm"
						component={FanMaturityModel}
					/>
					<PrivateRoute
						exact
						path="/settings/fc-configs"
						component={FactCubesSettings}
					/>
					<Redirect to="/signin" />
				</Switch>
			</Suspense>
		</Layout>
	);
}

export default function Routes() {
	return (
		<BrowserRouter
			getUserConfirmation={(message, callback) =>
				Confirmation(message, callback)
			}
		>
			<ErrorBoundary
				fallback={
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100vh",
							width: "100vw",
							background: "#f0f0f0",
							color: "#000",
							fontSize: "20px",
						}}
					>
						<CustomErrorPage />
					</div>
				}
				onError={(error, info) => {
					console.error(error, info);
				}}
			>
				<AppWithLoader />
			</ErrorBoundary>
		</BrowserRouter>
	);
}
